/*!
  Pioneer WFRIA ModalSelect v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ModalSelect"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ModalSelect") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ModalSelect");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "container", "customStrings", "disabled", "elementToFocusOnClose", "fieldColSize", "findBy", "findByContainerAriaLabel", "id", "inlineFilter", "label", "labelClassName", "labelColSize", "minSelection", "maxSelection", "name", "noAsterisk", "onClose", "onSave", "open", "panelHeaderDockAriaLabel", "panelWidth", "panelFeedBackError", "required", "selectionDetailsMessage", "selectedItemsCount", "selectionItemCounter", "trigger"],
      _excluded2 = ["cssRoot", "cssCancelButton", "cssPanel", "cssPanelBodyTitle", "cssInlineFilterWrapper", "cssFindByBoundingBox", "cssFindByWrapper", "cssPaginatorWrapper"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import filter from "../common/filter";
import { useUniqueId } from "../common/hooks";
import { requiredIf, validChild } from "../common/proptypes";
import { renderRequiredIcon } from "../common/utilities";
import { FormContext } from "../Form/FormContext";
import PanelSelectionsDock from "../private/PanelSelectionsDock/PanelSelectionsDock";
import Table from "../Table/Table";
import extractTheme from "./extractTheme";
import ModalSelectTrigger from "./ModalSelectTrigger";
import STRINGS from "./strings";
import { Button, FindBy, Flexgrid, FlexgridItem, IconError, IconInformationalLight, InlineFilter, Panel, Toolbar } from "..";
/**
 * ModalSelect is a React component that provides
 * the user the ability to select more than one
 * option from a list of options.
 * It replaces the native HTML select control
 * that allows multiple selections.
 */

/* eslint-disable-next-line complexity */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function ModalSelect(_ref) {
  let {
    children,
    className,
    container,
    customStrings,
    disabled,
    elementToFocusOnClose,
    fieldColSize,
    findBy,
    findByContainerAriaLabel,
    id: hiddenInputId,
    inlineFilter,
    label,
    labelClassName,
    labelColSize,
    minSelection,
    maxSelection,
    name,
    noAsterisk,
    onClose,
    onSave,
    open,
    panelHeaderDockAriaLabel,
    panelWidth,
    panelFeedBackError,
    required,
    selectionDetailsMessage,
    selectedItemsCount,
    selectionItemCounter,
    trigger
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();

  const _extractTheme = extractTheme(_objectSpread({
    theme,
    disabled
  }, otherProps)),
        {
    cssRoot,
    cssCancelButton,
    cssPanel,
    cssPanelBodyTitle,
    cssInlineFilterWrapper,
    cssFindByBoundingBox,
    cssFindByWrapper,
    cssPaginatorWrapper
  } = _extractTheme,
        otherCSS = _objectWithoutProperties(_extractTheme, _excluded2);

  const tableProps = children.props;
  const {
    data: tableData,
    selectable: tableSelectableProps,
    totalRows,
    paginator,
    controlled,
    caption,
    ariaCaption
  } = tableProps;

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    addPanelTitle,
    applyButtonLabel,
    cancelButtonLabel,
    closeButtonAriaLabel,
    editPanelTitle,
    errorMessage,
    findButtonAriaLabel,
    findAnnouncement,
    feedbackInstruction,
    feedbackMinSelectionError,
    feedbackMaxSelectionError,
    panelBodyTitle,
    selectedCountMessage,
    summaryText,
    toolbarAriaLabel,
    defaultInstruction,
    getMinOnlyInstruction,
    getMaxOnlyInstruction,
    getMinAndMaxInstruction,
    getMinErrorForMinMaxConfig,
    getMaxErrorForMinMaxConfig,
    getMinMaxSameInstruction
  } = componentStrings;
  let requiredMessage = null;

  if (typeof errorMessage === "string") {
    requiredMessage = errorMessage;
  } else {
    requiredMessage = errorMessage(label);
  }

  const tableDataRef = useRef([...tableData]);
  const animateModalSelect = container !== null;
  const componentPrefix = "pnr-modal-select";
  const instanceId = useUniqueId();
  const paginatorContainerId = `${componentPrefix}__paginator-container-${instanceId}`;
  const feedbackId = `${componentPrefix}-panel-feedback-${instanceId}`;
  const selectedCountId = `${componentPrefix}-panel-selected-count-${instanceId}`;
  const panelBodyRef = useRef();
  const findByInputElementRef = useRef();
  const paginatorContainerRef = useRef();
  const modalSelectTrigggerContainerRef = useRef(document.createElement("div"));
  const minSelectedAtLeastOnce = useRef(false);
  const maxSelectedAtLeastOnce = useRef(false);
  const selectedAtLeastOnce = useRef(false);
  /**
   * Panel title will be set in `useEffect`,
   * hence marking it has empty strings.
   */

  const panelTitle = useRef(" ");
  const {
    validateField,
    resetDone
  } = useContext(FormContext);
  const [selectedRows, setSelectedRows] = useState((tableSelectableProps === null || tableSelectableProps === void 0 ? void 0 : tableSelectableProps.selectedRows) || []);
  const [previousSelectedRows, setPreviouslySelectedRows] = useState(selectedRows);
  const [panelBodyRole, setPanelBodyRole] = useState("document");
  const [findByLookUpString, setFindByLookUpString] = useState("");
  const [findByFilteredData, setFindByFilteredData] = useState(null);
  const [findByAnnouncement, setFindByAnnouncement] = useState("");
  const intialSelectedRows = useRef([...selectedRows]);
  /**
   * Returns whether the Feedback should be hidden in ModalSelect Panel.
   * @param {data} table data.
   * @param {tableSelectableProps} table selectable props.
   */

  const getFeedbackVisibility = (data, tableSelectableProps) => {
    const dataLength = data.length;
    const selectionTypeSingle = tableSelectableProps && tableSelectableProps.selectionType === "single";
    return !dataLength || selectionTypeSingle;
  };
  /**
   * Handler called on click of Close button/Close icon in Panel.
   */


  const handleClose = () => {
    if (controlled) {
      onClose();
    } else {
      if (selectedAtLeastOnce.current) {
        validateField(name);
      }

      minSelectedAtLeastOnce.current = false;
      maxSelectedAtLeastOnce.current = false;
      selectedAtLeastOnce.current = false;
      setSelectedRows(previousSelectedRows);
      setFindByFilteredData(null);
      setFindByLookUpString("");
      onClose(selectedRows);
    }
  };

  const triggerId = () => {
    return trigger && trigger.props.id ? trigger.props.id : instanceId;
  };
  /**
   * Returns the Feedback details for minimum selection scenario
   * @returns Object Returns an object with Feedback message and error details.
   */


  const getMinSelectionFeedbackMessage = () => {
    let error = false,
        feedbackMessage = feedbackInstruction || getMinOnlyInstruction(minSelection);
    const selectedRowsLength = selectedRows.length;

    if (minSelectedAtLeastOnce.current && selectedRowsLength < minSelection) {
      feedbackMessage = feedbackMinSelectionError || feedbackMessage;
      error = true;
    }

    return {
      feedbackMessage,
      error
    };
  };
  /**
   * Returns the Feedback details for maximum selection scenario
   * @returns Object Returns an object with Feedback message and error details.
   */


  const getMaxSelectionFeedbackMessage = () => {
    let error = false,
        feedbackMessage = feedbackInstruction || getMaxOnlyInstruction(maxSelection);
    const selectedRowsLength = selectedRows.length;

    if (maxSelectedAtLeastOnce.current && selectedRowsLength > maxSelection) {
      feedbackMessage = feedbackMaxSelectionError || feedbackMessage;
      error = true;
    }

    return {
      feedbackMessage,
      error
    };
  };
  /**
   * Returns the Feedback details when both minimum and maximum selection are configured.
   * @returns Object Returns an object with Feedback message and error details.
   */


  const getMinMaxSelectionFeedbackMessage = () => {
    const selectedRowsLength = selectedRows.length;
    let error = false,
        feedbackMessage = feedbackInstruction || getMinAndMaxInstruction(minSelection, maxSelection);

    if (minSelectedAtLeastOnce.current && selectedRowsLength < minSelection) {
      feedbackMessage = feedbackMinSelectionError || getMinErrorForMinMaxConfig(minSelection);
      error = true;
    } else if (maxSelectedAtLeastOnce.current && selectedRowsLength > maxSelection) {
      feedbackMessage = feedbackMaxSelectionError || getMaxErrorForMinMaxConfig(maxSelection);
      error = true;
    }

    return {
      feedbackMessage,
      error
    };
  };
  /**
   * Returns the Feedback details when both minimum and maximum selection are same.
   * @param {number} minSelection - minimum selections required.
   * @param {number} maxSelection - maximum selections required.
   * @param {number} selectedRowsLength - selected records length.
   * @returns Object Returns an object with Feedback message and error details.
   */


  const getMinMaxSameSelectionFeedbackMessage = (minSelection, maxSelection, selectedRowsLength) => {
    let error = false,
        feedbackMessage = feedbackInstruction || getMinMaxSameInstruction(minSelection);

    if (minSelectedAtLeastOnce.current && (selectedRowsLength < minSelection || selectedRowsLength > maxSelection)) {
      feedbackMessage = feedbackMinSelectionError || feedbackMessage;
      error = true;
    }

    return {
      feedbackMessage,
      error
    };
  };
  /**
   * Returns the Feedback details for ModalSelect Panel.
   * @returns Object Returns an object with Feedback message and icon.
   */


  const getFeedBackDetails = () => {
    const selectedRowsLength = selectedRows.length;

    if (controlled) {
      return {
        feedbackError: panelFeedBackError,
        feedbackMessage: feedbackInstruction !== null ? feedbackInstruction : defaultInstruction
      };
    } else {
      let feedbackMessage,
          error = false;

      if (minSelection || maxSelection) {
        if (minSelection && !maxSelection) {
          // When only minimumSelection is configured
          ({
            feedbackMessage,
            error
          } = getMinSelectionFeedbackMessage());
        } else if (!minSelection && maxSelection) {
          // When only maximumSelection is configured
          ({
            feedbackMessage,
            error
          } = getMaxSelectionFeedbackMessage());
        } else if (minSelection && maxSelection && minSelection !== maxSelection) {
          // When both minimum and maximum selections are configured
          ({
            feedbackMessage,
            error
          } = getMinMaxSelectionFeedbackMessage());
        } else {
          // When both minimum and maximum selections are configured with same value
          ({
            feedbackMessage,
            error
          } = getMinMaxSameSelectionFeedbackMessage(minSelection, maxSelection, selectedRowsLength));
        }
      } else {
        // When selections are required
        feedbackMessage = feedbackInstruction !== null ? feedbackInstruction : defaultInstruction;

        if (selectedAtLeastOnce.current && required && selectedRowsLength === 0) {
          error = true;
        }
      }

      return {
        feedbackMessage,
        feedbackError: error
      };
    }
  };
  /**
   * Handler called on click of `Apply` button in ModalSelect Panel.
   */


  const handleSave = () => {
    if (controlled) {
      onSave && onSave();
    } else {
      onSave && onSave(selectedRows);
      setSelectedRows([...selectedRows]);
      setPreviouslySelectedRows([...selectedRows]);
    }

    validateField(name);
  };
  /**
   * This method saves the value of the input field.
   * @param {String} value of the field.
   */


  const handleFindByInputChange = _ref2 => {
    let {
      value
    } = _ref2;
    setFindByLookUpString(value);
  };
  /**
   * This method clears the input field and
   * focus the field.
   * @param {Node} focusInputField whether to focus the field or not.
   */


  const handleFindByClear = focusInputField => {
    setFindByLookUpString("");
    focusInputField && findByInputElementRef.current.focus();
  };
  /**
   * This method filters the records that are matching the
   * search string.
   */


  const handleFilter = () => {
    const filteredData = filter(tableDataRef.current, findByLookUpString, {
      fullTextMatch: false
    });
    setFindByFilteredData([...filteredData]);
    setFindByAnnouncement(findAnnouncement(filteredData.length));
  };
  /**
   * As per a11y requirements, this method will allow to
   * toggle the `role` property
   * of Panel body from `document` to `presentation`
   * whenever it gets focused.
   * @param {Event} e - focus event facade.
   */


  const handleFocus = _ref3 => {
    let {
      target
    } = _ref3;
    const panelBodyNode = panelBodyRef.current.querySelector(".pnr-panel__body");
    setPanelBodyRole(target === panelBodyNode ? "presentation" : "document");
  };
  /**
   * Handler called on selecting/unselecting records in table.
   * @param {array} selectedRows - holds array of selected row `rowKey` values.
   */


  const handleEditSelect = _ref4 => {
    let {
      selectedRows: choosenRows
    } = _ref4;
    const selectedRowsLength = choosenRows.length;

    if (minSelection || maxSelection) {
      if (!minSelectedAtLeastOnce.current && minSelection <= selectedRowsLength) {
        /*
         * Flag to track if minimum selections are made at least once.
         * It is used to display feedback details in ModalSelect Panel.
         */
        minSelectedAtLeastOnce.current = true;
      }

      if (!maxSelectedAtLeastOnce.current && maxSelection <= selectedRowsLength) {
        /*
         * Flag to track if maximum selections are made at least once.
         * It is used to display feedback details in ModalSelect Panel.
         */
        maxSelectedAtLeastOnce.current = true;
      }
    } else if (!selectedAtLeastOnce.current) {
      /*
       * Flag to track if selections are made at least once.
       * It is used to display feedback details in ModalSelect Panel.
       */
      selectedAtLeastOnce.current = Boolean(selectedRowsLength);
    }

    setSelectedRows(choosenRows);
  };
  /**
   * Returns FindBy Component.
   * @return React.Element
   */


  const getFindByComponent = () => {
    if (controlled) {
      return /*#__PURE__*/React.cloneElement(findBy);
    } else {
      return _jsx(FindBy, {
        name: `find-by-${name}`,
        "data-testid": "modalselect-findby",
        onFind: handleFilter,
        onClear: handleFindByClear,
        value: findByLookUpString,
        onValueChange: handleFindByInputChange,
        ref: findByInputElementRef,
        "aria-label": findByContainerAriaLabel,
        findAnnouncement: findByAnnouncement,
        customStrings: {
          findButtonAriaLabel,
          findByLabel: findByContainerAriaLabel
        }
      });
    }
  };
  /**
   * Returns the disability status of `Apply` button in ModalSelect Panel.
   * @return {Boolean}
   */


  const getApplyButtonState = () => {
    const selectedRowsCount = controlled ? selectedItemsCount || tableSelectableProps.selectedRows.length : selectedRows.length;
    let isApplyButtonDisabled = false;

    if (minSelection && selectedRowsCount < minSelection) {
      // When minimum selections are not made
      isApplyButtonDisabled = true;
    } else if (maxSelection && selectedRowsCount > maxSelection) {
      // When maximum selections are not made
      isApplyButtonDisabled = true;
    } else if (required && selectedRowsCount === 0) {
      // selections are not made when it is required
      isApplyButtonDisabled = true;
    }

    return isApplyButtonDisabled;
  };

  const getMessages = () => {
    const unsavedRecordsLength = controlled && (selectedItemsCount || tableSelectableProps.selectedRows.length) || selectedRows.length;
    const isSelectionTypeMulti = !tableSelectableProps || tableSelectableProps.selectionType !== "single";
    let itemsCountMessage = null,
        describedByOfPanel;

    if (isSelectionTypeMulti) {
      itemsCountMessage = selectedCountMessage(unsavedRecordsLength);
      describedByOfPanel = `${feedbackId} ${selectedCountId}`;
    } else {
      describedByOfPanel = feedbackId;
    }

    return {
      selectedItemsCountMessage: itemsCountMessage,
      panelDescribedBy: describedByOfPanel,
      footerApplyButtonLabel: selectionItemCounter && unsavedRecordsLength ? `${applyButtonLabel}: ${unsavedRecordsLength}` : `${applyButtonLabel}`
    };
  };

  const dataToBeRendered = controlled ? tableData : findByFilteredData || tableDataRef.current;
  const {
    selectedItemsCountMessage,
    panelDescribedBy,
    footerApplyButtonLabel
  } = getMessages();
  const feedbackVisibility = getFeedbackVisibility(dataToBeRendered, tableSelectableProps);
  /**
   * Returns the icon to render
   *
   * @param {String} feedbackMessage
   * @param {Boolean} feedbackError
   * @returns {Element | null}
   */

  const getFeedbackIcon = (feedbackMessage, feedbackError) => {
    if (feedbackMessage !== "") {
      return feedbackError ? _jsx(IconError, {
        size: "small"
      }) : _jsx(IconInformationalLight, {
        size: "small"
      });
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (container) {
      /**
       * Renders the ModalSelect within the specified `container` prop.
       */
      const modalSelectRootNode = document.querySelector(container);
      modalSelectRootNode && modalSelectRootNode.appendChild(modalSelectTrigggerContainerRef.current);
    }

    if (!open) {
      panelTitle.current = selectedRows.length ? editPanelTitle : addPanelTitle;
    }
  }, [open, container, editPanelTitle, addPanelTitle, selectedRows]);
  useEffect(() => {
    if (resetDone) {
      minSelectedAtLeastOnce.current = false;
      maxSelectedAtLeastOnce.current = false;
      selectedAtLeastOnce.current = false;
      setSelectedRows([...intialSelectedRows.current]);
    }
  }, [resetDone]);
  /**
   * getSelectedRows method return's the selected row's
   * id based on the usage of `ModalSelect` component
   * either in controlled or uncontrolle way.
   * @returns {Array} records to be displayed in table.
   */

  const getSelectedRows = () => {
    return controlled ? tableSelectableProps.selectedRows : [...selectedRows];
  };

  const getTotalRecordsCount = () => {
    return controlled ? totalRows : tableDataRef.current.length;
  };

  const {
    feedbackMessage,
    feedbackError
  } = getFeedBackDetails();
  const finalSelectedRows = getSelectedRows();

  const ModalSelectTriggerBody = _jsx(ModalSelectTrigger, {
    label: label,
    labelClassName: labelClassName,
    name: name,
    trigger: trigger,
    id: triggerId(),
    inputId: hiddenInputId,
    cssRoot: _objectSpread({}, otherCSS),
    labelColSize: labelColSize,
    fieldColSize: fieldColSize,
    renderAsterisk: renderRequiredIcon(required, noAsterisk, cssRoot === null || cssRoot === void 0 ? void 0 : cssRoot.showRequiredIcon),
    customStrings: {
      selectionDetailsMessage,
      errorMessage: requiredMessage,
      summaryText: summaryText(controlled ? tableSelectableProps.selectedRows.length : selectedRows.length, getTotalRecordsCount())
    },
    value: finalSelectedRows,
    required: required,
    open: open,
    className: className,
    disabled: disabled,
    selectedAtLeastOnce: selectedAtLeastOnce
  });

  return _jsxs("div", {
    css: cssRoot,
    children: [container ? /*#__PURE__*/ReactDOM.createPortal(ModalSelectTriggerBody, modalSelectTrigggerContainerRef.current) : ModalSelectTriggerBody, _jsxs(Panel, {
      onFocus: handleFocus,
      ref: panelBodyRef,
      animated: animateModalSelect,
      className: "pnr-modal-select__panel",
      customStrings: {
        closeButtonAriaLabel
      },
      customStyles: cssPanel,
      height: "100%",
      width: panelWidth,
      onClose: handleClose,
      open: open,
      title: panelTitle.current,
      elementToFocusOnClose: elementToFocusOnClose,
      closeTooltipPosition: "bottom-right",
      bodyRole: panelBodyRole,
      "aria-describedby": !feedbackVisibility ? panelDescribedBy : null,
      headerDockAriaLabel: panelHeaderDockAriaLabel,
      headerDock: tableSelectableProps.selectionType !== "single" && _jsxs("div", {
        className: "pnr-modal-select__header-dock",
        children: [panelBodyTitle && _jsx("h2", {
          css: cssPanelBodyTitle,
          children: panelBodyTitle
        }), _jsx(PanelSelectionsDock, {
          feedbackAriaLive: "polite",
          feedbackError: feedbackError,
          feedbackIcon: getFeedbackIcon(feedbackMessage, feedbackError),
          feedbackMessage: feedbackMessage,
          selectedCountMessage: selectedItemsCountMessage,
          feedbackId: feedbackId,
          selectedCountId: selectedCountId,
          noFeedback: feedbackVisibility
        })]
      }),
      footer: _jsxs(Flexgrid, {
        alignHorizontal: "flex-end",
        children: [_jsx(FlexgridItem, {
          css: cssCancelButton,
          children: _jsx(Button, {
            kind: "standard",
            label: cancelButtonLabel,
            onClick: handleClose
          })
        }), _jsx(FlexgridItem, {
          children: _jsx(Button, {
            kind: "primary",
            label: footerApplyButtonLabel,
            onClick: handleSave,
            disabled: getApplyButtonState()
          })
        })]
      }),
      children: [(findBy || paginator || inlineFilter) && _jsxs(Toolbar, {
        "aria-label": toolbarAriaLabel,
        className: "pnr-modal-select__toolbar",
        children: [inlineFilter && _jsx("div", {
          css: cssInlineFilterWrapper,
          children: inlineFilter
        }), _jsxs(Flexgrid, {
          alignHorizontal: "space-between",
          alignVertical: "flex-end",
          css: findBy || paginator ? cssFindByBoundingBox : {},
          children: [_jsx(FlexgridItem, {
            className: "pnr-modal-select__findby",
            css: cssFindByWrapper,
            children: findBy && getFindByComponent()
          }), paginator && _jsx(FlexgridItem, {
            css: cssPaginatorWrapper,
            children: _jsx("div", {
              id: paginatorContainerId,
              ref: paginatorContainerRef
            })
          })]
        })]
      }), /*#__PURE__*/React.cloneElement(children, {
        ariaCaption: ariaCaption || caption,
        caption: null,
        controlled,
        data: dataToBeRendered,
        nonStickyHeader: true,
        selectable: _objectSpread(_objectSpread({
          selectionType: "multi"
        }, tableProps.selectable), {}, {
          selectedRows: finalSelectedRows,
          onSelectedChange: controlled ? tableSelectableProps.onSelectedChange : handleEditSelect
        }),
        paginator: paginator && _objectSpread(_objectSpread({}, paginator), {}, {
          noHorizontalPadding: true,
          navigationTooltipPosition: "bottom-right",
          container: paginatorContainerRef
        })
      })]
    })]
  });
}

ModalSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * This prop identifies the element that should receive
   * focus after the ModalSelect panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /** The label for the ModalSelect. */
  label: PropTypes.node.isRequired,

  /** Unique name identifying this ModalSelect within a form. */
  name: PropTypes.string.isRequired,

  /** aria-label for Panel header dock container. */
  panelHeaderDockAriaLabel: PropTypes.string.isRequired,

  /**
   * This is what will be displayed inside the Panel.
   * It can only be of type Table.
   *
   * <ul>
   *   <li>
   *     `container` prop for Table paginator will be ignored even if it is passed in Table
   *      configuration. ModalSelect internally handles it by generating a container for it.
   *   </li>
   * </ul>
   */
  children: validChild.isRequired([Table]),

  /** Class name to ModalSelect trigger container. */
  className: PropTypes.string,

  /**
   * The container where the ModalSelect component will be rendered.
   * Defaults to the ModalSelect containing element.
   *
   * This should be used only in cases where ModalSelect is to be shown within Panel.
   * Please check with the Pioneer WFRIA team if you feel you need to use this
   * parameter in any other cases.
   */
  container: PropTypes.string,

  /** Override for strings used within the component. */
  customStrings: PropTypes.shape({
    /** aria-label for Panel's close Button. */
    closeButtonAriaLabel: PropTypes.string,

    /**
     * Renders tokenized text which will be displayed beside trigger element.
     * It receives total selection and total records as arguments.
     * Defaults to null.
     */
    summaryText: PropTypes.func,

    /**
     * Error message when there are no selections made.
     * Defaults to "Enter <component label>"
     */
    errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /**
     * Title of the Selection panel.
     * Defaults to "Add"
     */
    addPanelTitle: PropTypes.string,

    /**
     * Title of the Edit panel.
     * Defaults to "Edit"
     */
    editPanelTitle: PropTypes.string,

    /**
     * Title that will be displayed in Panel body.
     * Defaults to null.
     */
    panelBodyTitle: PropTypes.string,

    /**
     * Apply Button label that will be displayed in the ModalSelect Panel.
     * Defaults to "Apply"
     */
    applyButtonLabel: PropTypes.string,

    /**
     * Cancel Button label that will be displayed in the ModalSelect Panel.
     * Defaults to "Cancel"
     */
    cancelButtonLabel: PropTypes.string,

    /**
     * Render prop for the selected count message in the ModalSelect Panel.
     * It will be called with the selected rows count as an argument.
     * Defaults to `${count} selected` for `selection type multi` and `null` for `selection type single`.
     */
    selectedCountMessage: PropTypes.func,

    /**
     * Message to be shown in Feedback component.
     */
    feedbackInstruction: PropTypes.string,

    /**
     * Error message to display when the minimum selections have not been made.
     * It is not applicable if the `table` is controlled. Use `feedbackInstruction` instead.
     * Defaults to "Select at least ${count} items to continue"
     */
    feedbackMinSelectionError: PropTypes.string,

    /**
     * Error message to display when the maximum selections have been exceeded.
     * It is not applicable if the `table` is controlled. Use `feedbackInstruction` instead.
     * Defaults to "Select up to ${count} items to continue"
     */
    feedbackMaxSelectionError: PropTypes.string,

    /**
     * Text to be announced by the screen readers when "Go" button is triggered.
     * Applicable for only Uncontrolled ModalSelect with `FindBy` configured.
     * Defaults to "${filteredRecordCount} items found".
     * It will be called with the following argument:
     *
     * <ul>
     *  <li>
     *    `filteredRecordCount`: Number of rows filtered in the Edit Panel.
     *  </li>
     * </ul>
     */
    findAnnouncement: PropTypes.func,

    /**
     * Aria label which will be applied to "Go" button.
     * Applicable for only Uncontrolled ModalSelect with `FindBy` configured.
     * Defaults to "Go search data".
     */
    findButtonAriaLabel: PropTypes.string,

    /**
     * Aria label which will be applied to "Toolbar" component.
     * A descriptive label should be provided.
     * Defaults to no label.
     */
    toolbarAriaLabel: PropTypes.string
  }),

  /** Disables or enables the look of the field. */
  disabled: PropTypes.bool,

  /** CSS class name of the container that wraps summaryText and trigger. */
  fieldClassName: PropTypes.string,

  /** The number of columns required by the container that wraps trigger button and file details. */
  fieldColSize: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Renders the FindBy component in the ModalSelect panel.
   * `FindBy` component needs to be passed as prop value when `Table` is `controlled`.
   * Otherwise it is a `Boolean` value.
   */
  findBy: PropTypes.oneOfType([PropTypes.bool, validChild([FindBy])]),

  /**
   * Aria label which will be applied to the FindBy container.
   * Applicable for only Uncontrolled ModalSelect with `FindBy` configured.
   */
  findByContainerAriaLabel: requiredIf(PropTypes.string, (props, propName) => props.findBy && typeof props.findBy === "boolean" && !props[propName], "if `findBy` is configured"),

  /**
   * [InlineFilter](#/Components/InlineFilter) element that is displayed inside the ModalSelect panel.
   */
  inlineFilter: validChild([InlineFilter]),

  /** CSS class name of the container that wraps the label. */
  labelClassName: PropTypes.string,

  /** The number of columns required by the container that wraps the label. */
  labelColSize: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Maximum number of selections to be made in the ModalSelect panel.
   */
  maxSelection: PropTypes.number,

  /**
   * Minimum number of selections to be made in the ModalSelect panel.
   */
  minSelection: PropTypes.number,

  /**
   * Determines whether to add an asterisk to the left of the label of a required ModalSelect field.
   */
  noAsterisk: PropTypes.bool,

  /**
   * Callback to be executed on click of Close icon Button/Cancel Button in ModalSelect panel.
   * It receives an array of unique id's when `controlled` is not set to table.
   * @param {savedData} array of unique id's.
   */
  onClose: PropTypes.func,

  /**
   * Callback to be executed after click of Apply Button in ModalSelect panel.
   * It receives an array of unique id's when `controlled` is not set to table.
   * @param {savedData} array of unique id's.
   */
  onSave: PropTypes.func,

  /** Opens/Closes the ModalSelect Panel */
  open: PropTypes.bool,

  /**
   * To show error icon in ModalSelect Panel.
   * It is applicable only when `controlled` is set to `true` for table.
   */
  panelFeedBackError: PropTypes.bool,

  /**
   * The width of the Panel at a desktop breakpoint. Must include the unit type. i.e. 200px or 40%.
   * <br/> **Note**: Regardless of the value entered,
   * the calculated width will always be lower than 80% of the viewport width.
   */
  panelWidth: PropTypes.string,

  /** Determines whether the selections are mandatory or not. */
  required: PropTypes.bool,

  /**
   * Selected records count that is displayed in `Apply` button
   * label and Panel dock. Applicable only for `controlled` ModalSelect.
   *
   */
  selectedItemsCount: PropTypes.number,

  /**
   * Selection details that will be displayed under the trigger element.
   */
  selectionDetailsMessage: PropTypes.func,

  /**
   * Displays the selected item count in Apply Button in ModalSelect Panel.
   */
  selectionItemCounter: PropTypes.bool,

  /** Button which launches the ModalSelect panel. */
  trigger: validChild([Button])
} : {};
ModalSelect.defaultProps = {
  children: null,
  className: null,
  trigger: null,
  labelClassName: null,
  fieldClassName: null,
  fieldColSize: {
    xs: 12,
    sm: 8
  },
  container: null,
  onClose: null,
  onSave: null,
  open: false,
  required: false,
  disabled: false,
  labelColSize: {
    xs: 12,
    sm: 4
  },
  selectionDetailsMessage: null,
  selectionItemCounter: false,
  customStrings: {},
  minSelection: 0,
  maxSelection: 0,
  findBy: false,
  panelFeedBackError: false,
  findByContainerAriaLabel: null,
  inlineFilter: null,
  noAsterisk: false,
  panelWidth: "500px",
  selectedItemsCount: null
};
ModalSelect.displayName = "ModalSelect";
export default ModalSelect;